// Direct browser-to-R2 uploads. Only temporary signed URLs are sent to the
// browser; the R2 S3 secret NEVER leaves the shared Supabase Edge Function.
export const R2_ENDPOINT='https://hunbyluftwefqhdegboh.supabase.co/functions/v1/aaf-r2-media';
export const R2_MAX_MEDIA_BYTES=5_000_000_000; // Controlled beta UI ceiling; R2 service enforces its own quota.
const RETRIES=3;
const delay=ms=>new Promise(resolve=>setTimeout(resolve,ms));
export async function r2Request(client,payload){
 const {data,error}=await client.auth.getSession();
 if(error||!data?.session?.access_token)throw new Error('Sign in again to access your R2 media.');
 const response=await fetch(R2_ENDPOINT,{
  method:'POST',headers:{'Content-Type':'application/json',Authorization:'Bearer '+data.session.access_token},
  body:JSON.stringify({...payload,app:'takefold'}),cache:'no-store'
 });
 const result=await response.json().catch(()=>({error:'The R2 service did not return a valid response.'}));
 if(!response.ok)throw new Error(result.error||'R2 media request failed (HTTP '+response.status+').');
 return result;
}
function put(url,blob,type,onProgress){
 return new Promise((resolve,reject)=>{
  const xhr=new XMLHttpRequest();
  xhr.open('PUT',url);
  // Single PUT signs Content-Type. Multipart PUT does not; do not supply it.
  if(type)xhr.setRequestHeader('Content-Type',type);
  xhr.upload.onprogress=e=>{if(e.lengthComputable)onProgress(e.loaded);};
  xhr.onerror=()=>reject(new Error('R2 upload network failure. Check your connection and retry.'));
  xhr.onabort=()=>reject(new Error('R2 upload cancelled.'));
  xhr.onload=()=>{
   if(xhr.status<200||xhr.status>=300)return reject(new Error('R2 rejected a video chunk (HTTP '+xhr.status+'). Check bucket CORS and credentials.'));
   onProgress(blob.size);
   resolve(xhr.getResponseHeader('ETag')||'');
  };
  xhr.send(blob);
 });
}
export async function uploadR2(client,file,projectId,onProgress=()=>{}){
 if(!file||file.size<1||file.size>R2_MAX_MEDIA_BYTES)
  throw new Error('Choose an MP4 or WebM video up to 5 GB for the current beta.');
 const auth=payload=>r2Request(client,{...payload,projectId});
 const type=file.type||(/\.webm$/i.test(file.name)?'video/webm':'video/mp4');
 const job=await auth({action:'start',name:file.name,type,size:file.size});
 const progress=new Map(),parts=[];
 const emit=(i,n)=>{progress.set(i,n);onProgress(Math.floor([...progress.values()].reduce((sum,v)=>sum+v,0)/file.size*100));};
 let succeeded=false;
 try{
  if(job.mode==='single'){
   for(let attempt=0;attempt<RETRIES;attempt++){
    try{
     await put(job.url,file,type,n=>emit(0,n));break;
    }catch(e){progress.delete(0);if(attempt===RETRIES-1)throw e;await delay(600*2**attempt);}
   }
  }else if(job.mode==='multipart'){
   const length=job.partBytes,count=Math.ceil(file.size/length);
   if(count!==job.parts||count>10000||length<5*1024*1024)throw new Error('Invalid R2 multipart upload settings.');
   let index=0;
   const worker=async()=>{
    while(index<count){
     const i=index++,chunk=file.slice(i*length,Math.min(file.size,(i+1)*length));
     for(let attempt=0;attempt<RETRIES;attempt++){
      try{
       const {url}=await auth({action:'part',id:job.id,partNumber:i+1});
       const tag=await put(url,chunk,null,n=>emit(i,n));
       if(!/^"?[\da-f]{32}"?$/i.test(tag))
        throw new Error('R2 did not expose the multipart ETag. Check the takefold-media bucket CORS ExposeHeaders.');
       parts[i]={partNumber:i+1,etag:tag};
       break;
      }catch(e){
       emit(i,0);
       if(attempt===RETRIES-1)throw e;
       await delay(600*2**attempt);
      }
     }
    }
   };
   await Promise.all(Array.from({length:Math.min(3,count)},()=>worker()));
  }else throw new Error('Unrecognised R2 upload mode.');
  const finished=await auth({action:'complete',id:job.id,parts:job.mode==='multipart'?parts:undefined});
  if(finished.provider!=='r2'||finished.status!=='ready'||finished.bytes!==file.size||!finished.id)
   throw new Error('R2 could not confirm the uploaded video.');
  succeeded=true;onProgress(100);return finished;
 }finally{
  if(!succeeded)await auth({action:'abort',id:job.id}).catch(()=>{});
 }
}
export const isR2Row=row=>row?.data?.mediaProvider==='r2'&&Boolean(row?.data?.r2UploadId);
export async function r2SignedUrl(client,row){
 if(!isR2Row(row))throw new Error('This video has no R2 upload reference.');
 const {url}=await r2Request(client,{action:'read',id:row.data.r2UploadId});
 if(!/^https:\/\/[^/]+\.r2\.cloudflarestorage\.com\//i.test(url||''))
  throw new Error('R2 returned an unexpected playback URL.');
 return url;
}
